import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Users, DollarSign, Activity, ArrowUpRight } from 'lucide-react';
interface HeroProps {
  onOpenModal: () => void;
}
export function Hero({
  onOpenModal
}: HeroProps) {
  return <section className="relative min-h-screen pt-32 pb-20 flex flex-col items-center justify-center overflow-hidden" data-id="element-122">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" data-id="element-123" />

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full" data-id="element-124">
        <div className="text-center max-w-4xl mx-auto mb-16" data-id="element-125">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-6xl md:text-8xl font-bold tracking-tight mb-6 text-gradient" data-id="element-126">
            Stop. See. Scale.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.15,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-lg md:text-2xl text-textSecondary mb-10 font-medium max-w-2xl mx-auto" data-id="element-127">
            Your whole business. One dashboard.
            <br className="hidden md:block" data-id="element-128" />
            Custom-built and managed for you.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-129">
            <button onClick={onOpenModal} className="w-full sm:w-auto bg-white text-black font-medium px-8 py-4 rounded-full hover:bg-white/90 transition-colors text-lg" data-id="element-130">
              Build My Dashboard
            </button>
            <button className="w-full sm:w-auto glass-panel text-white font-medium px-8 py-4 rounded-full hover:bg-white/5 transition-colors text-lg flex items-center justify-center gap-2" data-id="element-131">
              See It In Action
            </button>
          </motion.div>
        </div>

        {/* Animated Dashboard Preview */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative max-w-5xl mx-auto" data-id="element-132">
          {/* Mobile: phone-framed dashboard */}
          <div className="md:hidden relative flex justify-center" data-id="element-133">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px] h-[320px] bg-accent/10 rounded-full blur-[80px] pointer-events-none" data-id="element-134" />
            <div className="relative w-[270px] rounded-[2.5rem] border-[10px] border-[#111] bg-background shadow-[0_20px_60px_rgba(0,0,0,0.6)] accent-glow overflow-hidden" data-id="element-135">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-5 bg-[#111] rounded-b-2xl z-10" data-id="element-136" />
              <div className="pt-8 pb-5 px-4" data-id="element-137">
                <div className="text-xs text-textSecondary font-medium mb-4 text-center" data-id="element-138">
                  Command Center
                </div>
                {/* Stacked metric cards */}
                <div className="grid grid-cols-2 gap-2.5 mb-3" data-id="element-139">
                  {[{
                  label: 'MRR',
                  value: '$124.5k',
                  trend: '+12.5%'
                }, {
                  label: 'Clients',
                  value: '842',
                  trend: '+5.2%'
                }, {
                  label: 'ROI',
                  value: '3.2x',
                  trend: '+0.4x'
                }, {
                  label: 'Close',
                  value: '28.4%',
                  trend: '+2.1%'
                }].map((m, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.4,
                  delay: 0.8 + i * 0.08
                }} className="bg-card border border-white/5 rounded-lg p-2.5" data-id="element-140">
                      <div className="text-base font-semibold leading-tight" data-id="element-141">
                        {m.value}
                      </div>
                      <div className="flex items-center justify-between mt-0.5" data-id="element-142">
                        <span className="text-[10px] text-textSecondary" data-id="element-143">
                          {m.label}
                        </span>
                        <span className="text-[10px] text-green-400 flex items-center" data-id="element-144">
                          <ArrowUpRight size={10} data-id="element-145" />
                          {m.trend}
                        </span>
                      </div>
                    </motion.div>)}
                </div>
                {/* Mini chart */}
                <div className="bg-card border border-white/5 rounded-lg p-3 mb-3" data-id="element-146">
                  <div className="text-[10px] text-textSecondary mb-2" data-id="element-147">
                    Revenue vs Target
                  </div>
                  <div className="flex items-end gap-1.5 h-20" data-id="element-148">
                    {[40, 55, 45, 70, 65, 85, 80, 100].map((h, i) => <motion.div key={i} initial={{
                    height: 0
                  }} animate={{
                    height: `${h}%`
                  }} transition={{
                    duration: 0.8,
                    delay: 1.2 + i * 0.08,
                    ease: 'easeOut'
                  }} className="flex-1 bg-gradient-to-t from-accent/20 to-accent/80 rounded-t-sm" data-id="element-149" />)}
                  </div>
                </div>
                {/* Lead sources */}
                <div className="bg-card border border-white/5 rounded-lg p-3 space-y-2.5" data-id="element-150">
                  <div className="text-[10px] text-textSecondary" data-id="element-151">
                    Lead Sources
                  </div>
                  {[{
                  name: 'Organic',
                  val: 45
                }, {
                  name: 'Meta Ads',
                  val: 30
                }, {
                  name: 'Referral',
                  val: 15
                }].map((s, i) => <div key={i} data-id="element-152">
                      <div className="flex justify-between text-[10px] mb-1" data-id="element-153">
                        <span data-id="element-154">{s.name}</span>
                        <span className="text-textSecondary" data-id="element-155">{s.val}%</span>
                      </div>
                      <div className="h-1 bg-secondary rounded-full overflow-hidden" data-id="element-156">
                        <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: `${s.val}%`
                    }} transition={{
                      duration: 0.8,
                      delay: 1.8 + i * 0.15
                    }} className="h-full bg-white/80 rounded-full" data-id="element-157" />
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop: full dashboard */}
          <div className="hidden md:block glass-panel rounded-2xl p-2 accent-glow" data-id="element-158">
            <div className="bg-background rounded-xl overflow-hidden border border-white/5" data-id="element-159">
              {/* Dashboard Header */}
              <div className="border-b border-white/5 p-4 flex items-center justify-between bg-secondary/50" data-id="element-160">
                <div className="flex items-center gap-4" data-id="element-161">
                  <div className="w-3 h-3 rounded-full bg-accent/20 border border-accent/60" data-id="element-162" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50" data-id="element-163" />
                  <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50" data-id="element-164" />
                </div>
                <div className="text-sm text-textSecondary font-medium" data-id="element-165">
                  Executive Command Center
                </div>
                <div className="w-20" data-id="element-166" />
              </div>

              {/* Dashboard Content */}
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-167">
                {/* Metric Cards */}
                <div className="col-span-1 md:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-168">
                  {[{
                  label: 'MRR',
                  value: '$124,500',
                  icon: DollarSign,
                  trend: '+12.5%'
                }, {
                  label: 'Active Clients',
                  value: '842',
                  icon: Users,
                  trend: '+5.2%'
                }, {
                  label: 'Marketing ROI',
                  value: '3.2x',
                  icon: Activity,
                  trend: '+0.4x'
                }, {
                  label: 'Close Rate',
                  value: '28.4%',
                  icon: TrendingUp,
                  trend: '+2.1%'
                }].map((metric, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.5,
                  delay: 0.8 + i * 0.1
                }} className="bg-card border border-white/5 rounded-xl p-4" data-id="element-169">
                      <div className="flex items-center justify-between mb-3" data-id="element-170">
                        <metric.icon size={16} className="text-textSecondary" data-id="element-171" />
                        <span className="text-xs text-green-400 flex items-center gap-1" data-id="element-172">
                          <ArrowUpRight size={12} data-id="element-173" /> {metric.trend}
                        </span>
                      </div>
                      <div className="text-2xl font-semibold mb-1" data-id="element-174">
                        {metric.value}
                      </div>
                      <div className="text-xs text-textSecondary" data-id="element-175">
                        {metric.label}
                      </div>
                    </motion.div>)}
                </div>

                {/* Main Chart Area */}
                <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 1,
                delay: 1.2
              }} className="col-span-1 md:col-span-2 bg-card border border-white/5 rounded-xl p-6 h-64 flex flex-col" data-id="element-176">
                  <div className="text-sm font-medium text-textSecondary mb-4" data-id="element-177">
                    Revenue vs Target
                  </div>
                  <div className="flex-1 relative flex items-end gap-2" data-id="element-178">
                    {[40, 55, 45, 70, 65, 85, 80, 100].map((h, i) => <motion.div key={i} initial={{
                    height: 0
                  }} animate={{
                    height: `${h}%`
                  }} transition={{
                    duration: 1,
                    delay: 1.5 + i * 0.1,
                    ease: 'easeOut'
                  }} className="flex-1 bg-gradient-to-t from-accent/20 to-accent/80 rounded-t-sm relative group" data-id="element-179">
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-180">
                          ${h}k
                        </div>
                      </motion.div>)}
                  </div>
                </motion.div>

                {/* Side Panel */}
                <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 1,
                delay: 1.4
              }} className="col-span-1 bg-card border border-white/5 rounded-xl p-6" data-id="element-181">
                  <div className="text-sm font-medium text-textSecondary mb-4" data-id="element-182">
                    Lead Sources
                  </div>
                  <div className="space-y-4" data-id="element-183">
                    {[{
                    name: 'Organic Search',
                    val: 45
                  }, {
                    name: 'Meta Ads',
                    val: 30
                  }, {
                    name: 'Referral',
                    val: 15
                  }, {
                    name: 'Direct',
                    val: 10
                  }].map((source, i) => <div key={i} data-id="element-184">
                        <div className="flex justify-between text-xs mb-1" data-id="element-185">
                          <span data-id="element-186">{source.name}</span>
                          <span className="text-textSecondary" data-id="element-187">
                            {source.val}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-secondary rounded-full overflow-hidden" data-id="element-188">
                          <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: `${source.val}%`
                      }} transition={{
                        duration: 1,
                        delay: 2 + i * 0.2
                      }} className="h-full bg-white/80 rounded-full" data-id="element-189" />
                        </div>
                      </div>)}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}